/**
 * Local-dev mock of a mid-shift re-plan getShiftPlanOutput response (RCC_MID_SHIFT_REPLAN).
 *
 * Values are the real FPS sample for plan 1629e307-a2b4-4b7e-916a-45b8b850bbc2 (night shift,
 * re-planned after 23:00): hours 18:00-23:00 are ACTUAL (FCLM), 00:00 onwards PROJECTED. The FPS
 * output has no 23:00-00:00 bucket and no 00:00 volume point; that gap is kept so local dev shows what
 * the backend actually returns today.
 *
 * Headcount rows are stored compactly as [intValue, hoursRequired] in LABEL order and expanded below.
 */

type Kind = "ACTUAL" | "PROJECTED";
type Row = [number, number];

const LABELS = [
  "C-Returns_Mezz",
  "C-Returns Lead/PA",
  "Team_Mech_Sort",
  "Team_Mech_Wspider",
  "Team_Mech_Unload",
  "C-Return Dock Scan",
  "CR Refurb Spec 10",
  "Customer Returns 5S",
  "Lead/PA - WD",
  "Primary Grading",
  "Advanced Grading Lite 1",
  "MLG Fast Processing",
  "Clean Decant",
  "WD Grading",
  "Water Spider",
  "C-Returns_Add-Ins",
  "C-Returns Refurbish",
  "C-Return Sort",
  "WD Water Spider",
  "C-Returns Prob Solve",
  "Decant Non-TI",
  "Process Guide C-Ret",
  "Unloader",
  "C-Return Audit",
  "Team_Mech_Audit",
];

const PROJECTED_03_04: Row[] = [
  [3, 3.0], [6, 6.0], [2, 2.0], [2, 2.0], [2, 2.0], [3, 3.0], [2, 2.0], [2, 2.0], [1, 1.0],
  [128, 127.88418065377675], [0, 0.0], [0, 0.0], [0, 0.0], [22, 21.072272220983738], [11, 10.657015054481397],
  [4, 3.5523383514937987], [4, 3.5523383514937987], [8, 7.1046767029875975], [4, 3.5120453701639565],
  [8, 7.992761290861047], [8, 7.1046767029875975], [2, 1.6340756416871471], [8, 7.1046767029875975],
  [0, 0.0], [0, 0.0],
];

const HEADCOUNT_WINDOWS: { startTime: string; endTime: string; dataKind: Kind; rows: Row[] }[] = [
  {
    startTime: "18:00", endTime: "19:00", dataKind: "ACTUAL",
    rows: [
      [5, 5.0], [9, 8.883333333333333], [1, 0.8608333333333333], [1, 0.06666666666666667], [3, 2.4166666666666665],
      [2, 2.0], [0, 0.0], [1, 1.0], [0, 0.0], [80, 79.4036111111111], [0, 0.0], [0, 0.0], [0, 0.0],
      [26, 25.658888888888885], [30, 29.434166666666663], [0, 0.0], [11, 10.864166666666666],
      [8, 7.782222222222222], [0, 0.0], [12, 11.933333333333334], [0, 0.0], [0, 0.0], [1, 0.9166666666666666],
      [0, 0.0], [7, 6.916666666666667],
    ],
  },
  {
    startTime: "19:00", endTime: "20:00", dataKind: "ACTUAL",
    rows: [
      [5, 5.0], [10, 9.8], [0, 0.0], [4, 3.5966666666666667], [5, 4.083888888888889], [2, 2.0], [0, 0.0],
      [1, 1.0], [0, 0.0], [85, 84.275], [0, 0.0], [0, 0.0], [0, 0.0], [31, 30.86500000000001],
      [31, 30.26722222222222], [0, 0.0], [11, 10.240555555555556], [10, 9.59361111111111], [0, 0.0],
      [14, 13.023888888888887], [0, 0.0], [0, 0.0], [1, 0.4572222222222222], [0, 0.0], [7, 6.785555555555556],
    ],
  },
  {
    startTime: "20:00", endTime: "21:00", dataKind: "ACTUAL",
    rows: [
      [5, 5.0], [12, 11.35], [2, 1.7688888888888887], [4, 3.281388888888889], [5, 5.0], [2, 2.0], [0, 0.0],
      [1, 1.0], [0, 0.0], [85, 83.20388888888886], [0, 0.0], [0, 0.0], [0, 0.0], [28, 27.88444444444445],
      [30, 29.24388888888889], [0, 0.0], [14, 13.274166666666668], [10, 9.115], [0, 0.0],
      [14, 13.509444444444444], [0, 0.0], [0, 0.0], [0, 0.0], [0, 0.0], [7, 6.5586111111111105],
    ],
  },
  {
    startTime: "21:00", endTime: "22:00", dataKind: "ACTUAL",
    rows: [
      [5, 4.083333333333333], [9, 8.783333333333333], [2, 1.3736111111111111], [2, 1.1833333333333333],
      [3, 2.916666666666667], [2, 1.5833333333333335], [0, 0.0], [1, 1.0], [0, 0.0], [60, 59.246111111111105],
      [0, 0.0], [0, 0.0], [0, 0.0], [23, 22.23083333333334], [21, 20.150000000000002], [0, 0.0],
      [11, 10.196388888888887], [8, 7.804722222222222], [0, 0.0], [10, 9.483333333333333], [0, 0.0], [0, 0.0],
      [0, 0.0], [1, 0.22083333333333333], [5, 4.005833333333333],
    ],
  },
  {
    startTime: "22:00", endTime: "23:00", dataKind: "ACTUAL",
    rows: [
      [3, 2.138333333333333], [9, 6.461111111111111], [2, 0.9299999999999999], [3, 1.9141666666666666],
      [4, 2.401111111111111], [1, 0.2127777777777778], [0, 0.0], [1, 0.7127777777777777], [0, 0.0],
      [69, 52.67666666666667], [0, 0.0], [0, 0.0], [0, 0.0], [27, 20.7], [14, 10.71722222222222], [0, 0.0],
      [10, 7.254166666666666], [10, 6.973333333333333], [0, 0.0], [10, 7.440555555555554], [0, 0.0], [0, 0.0],
      [0, 0.0], [1, 0.6066666666666666], [4, 2.567222222222222],
    ],
  },
  {
    startTime: "00:00", endTime: "01:00", dataKind: "PROJECTED",
    rows: [
      [3, 3.0], [6, 6.0], [2, 2.0], [2, 2.0], [2, 2.0], [3, 3.0], [2, 2.0], [2, 2.0], [1, 1.0],
      [129, 128.27700770416976], [0, 0.0], [0, 0.0], [0, 0.0], [22, 21.137000778490457], [11, 10.689750642014147],
      [4, 3.5632502140047158], [4, 3.5632502140047158], [8, 7.1265004280094315], [4, 3.5228334630817426],
      [9, 8.01731298151061], [8, 7.1265004280094315], [2, 1.639095098442169], [8, 7.1265004280094315],
      [0, 0.0], [0, 0.0],
    ],
  },
  {
    startTime: "01:00", endTime: "02:00", dataKind: "PROJECTED",
    rows: [
      [3, 1.5], [6, 3.0], [2, 1.0], [2, 1.0], [2, 1.0], [3, 1.5], [2, 1.0], [2, 1.0], [1, 0.5],
      [124, 61.7398397751853], [0, 0.0], [0, 0.0], [0, 0.0], [21, 10.173257583319412], [11, 5.144986647932108],
      [4, 1.7149955493107027], [4, 1.7149955493107027], [7, 3.4299910986214055], [4, 1.6955429305532352],
      [8, 3.8587399859490814], [7, 3.4299910986214055], [2, 0.7888979526829232], [7, 3.4299910986214055],
      [0, 0.0], [0, 0.0],
    ],
  },
  {
    startTime: "02:00", endTime: "03:00", dataKind: "PROJECTED",
    rows: [
      [3, 3.0], [6, 6.0], [2, 2.0], [2, 2.0], [2, 2.0], [3, 3.0], [2, 2.0], [2, 2.0], [1, 1.0],
      [128, 127.53889240443179], [0, 0.0], [0, 0.0], [0, 0.0], [22, 21.0153769275416], [11, 10.628241033702649],
      [4, 3.5427470112342165], [4, 3.5427470112342165], [8, 7.085494022468433], [4, 3.5025628212569333],
      [8, 7.971180775276987], [8, 7.085494022468433], [2, 1.6296636251677394], [8, 7.085494022468433],
      [0, 0.0], [0, 0.0],
    ],
  },
  { startTime: "03:00", endTime: "04:00", dataKind: "PROJECTED", rows: PROJECTED_03_04 },
  { startTime: "04:00", endTime: "17:30", dataKind: "PROJECTED", rows: PROJECTED_03_04 },
];

const VOLUME: { time: string; recommended: number; dataKind: Kind }[] = [
  { time: "19:00", recommended: 990.0, dataKind: "ACTUAL" },
  { time: "20:00", recommended: 2411.0, dataKind: "ACTUAL" },
  { time: "21:00", recommended: 3755.0, dataKind: "ACTUAL" },
  { time: "22:00", recommended: 4568.0, dataKind: "ACTUAL" },
  { time: "23:00", recommended: 5278.0, dataKind: "ACTUAL" },
  { time: "01:00", recommended: 7237.289833668486, dataKind: "PROJECTED" },
  { time: "02:00", recommended: 8180.297801883741, dataKind: "PROJECTED" },
  { time: "03:00", recommended: 10128.313738314251, dataKind: "PROJECTED" },
  { time: "04:00", recommended: 12081.603571982738, dataKind: "PROJECTED" },
  { time: "05:00", recommended: 14034.893405651224, dataKind: "PROJECTED" },
];

/**
 * Operator Override plan: same actuals (same shift), projection 8% higher — as if the operator raised
 * the volume target on the re-plan. Derived for local dev only so the orange and black lines differ.
 */
const OVERRIDE_PROJECTION_FACTOR = 1.08;

const LIVE_VOLUME_DATA = [
  ["09:30", 0, 0], ["09:45", 0, 5], ["10:00", 5, 15], ["10:15", 30, 30], ["10:30", 35, 35],
  ["10:45", 25, 30], ["11:00", 27, 30], ["11:15", 10, 30], ["11:30", 25, 28], ["11:45", 23, 25],
  ["12:00", 20, 20], ["12:15", 5, 15], ["12:30", 17, 10], ["12:45", 15, 5], ["13:00", 8, 0],
].map(([time, wip, shift]) => ({ time, wip, shift }));

/** The mid-shift volume curve; the override plan's projected points are scaled up. */
const midShiftVolume = (isOverridePlan: boolean) =>
  VOLUME.map((point) =>
    isOverridePlan && point.dataKind === "PROJECTED"
      ? { ...point, recommended: point.recommended * OVERRIDE_PROJECTION_FACTOR }
      : { ...point }
  );

/** Builds the mid-shift getShiftPlanOutput response for the given plan id. */
export const buildMidShiftShiftPlanOutput = (planId: string, isOverridePlan: boolean) => ({
  planId,
  planStatus: "PENDING_APPROVAL",
  clockWindow: "DAY",
  primaryGradingVolume: {
    recommended: { total: 14034.893405651223, alps: 12761.123631162616 },
    override: { total: 0.0, alps: 12761.123631162616 },
  },
  overrides: { total: 0.0, average: 0.0 },
  totalOverrides: 0,
  backlogProjections: {
    endOfShift: { total: 0.0, alps: 0.0 },
    endOfWeek: { total: 0.0, alps: 0.0 },
  },
  shiftTotals: {
    headcount: null,
    hours: null,
    problemSolveVolume: null,
    amazonResaleVolume: null,
    vetAcceptedHours: { intValue: 0 },
    vtoAcceptedHours: { intValue: 630 },
    amazonReadyAcceptedHours: { intValue: 525 },
    showHoursNeeded: { intValue: -83 },
    laborHoursDeltaPercent: 3.785529001302309,
    regularScheduledHours: { intValue: 2584 },
    totalAvailableHours: { intValue: 2203 },
    laborShareHours: { intValue: -359 },
    miscHours: { intValue: -52 },
    vto: { actual: 60.0, recommended: 0.0 },
    vet: { actual: 74.0, recommended: 0.0 },
  },
  // Same series on both fields, as the backend sends it during the migration.
  primaryGradingVolumeTrend: midShiftVolume(isOverridePlan),
  projectedPrimaryGradingVolume: midShiftVolume(isOverridePlan),
  liveVolumeData: LIVE_VOLUME_DATA,
  shiftNotes: [],
  shiftTimeline: [
    { label: "Start Shift", startTime: "09:00", endTime: null },
    { label: "break 1", startTime: "9:15", endTime: "9:30" },
  ],
  shiftSummary: [
    {
      heading: "headcount",
      planOutput: HEADCOUNT_WINDOWS.map(({ startTime, endTime, dataKind, rows }) => ({
        startTime,
        endTime,
        data: rows.map(([intValue, hoursRequired], i) => ({ label: LABELS[i], intValue, hoursRequired })),
        dataKind,
      })),
    },
  ],
});
